import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/d9q4fpcnlsngrm4llr9g';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const hpValue = formData.get('company_alt') as string;
    if (hpValue && hpValue.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: '诚信为本',
      desc: '坚持合规经营，公开透明，对每一位用户负责。',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: '技术创新',
      desc: '持续研发投入，用前沿技术提升购彩体验。',
    },
    {
      icon: 'ri-heart-line',
      title: '服务至上',
      desc: '7×24小时专业客服，用心服务每一位彩民。',
    },
    {
      icon: 'ri-lock-line',
      title: '安全第一',
      desc: '银行级数据加密，全方位保障用户信息安全。',
    },
  ];

  const certs = [
    '国家彩票销售许可',
    '信息系统安全等级保护三级',
    'ISO 27001信息安全管理认证',
    'AAA级信用企业认证',
    '网络文化经营许可证',
  ];

  const stats = [
    { value: '500万+', label: '累计用户' },
    { value: '1.2亿+', label: '累计订单' },
    { value: '89亿+', label: '累计兑奖' },
    { value: '6年', label: '稳定运营' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <Breadcrumb items={[{ label: '关于我们', path: '/about' }]} />
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                关于BB彩票平台
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                安全、正规、值得信赖的手机彩票购买平台
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20clean%20office%20environment%20with%20warm%20golden%20and%20beige%20tones%2C%20professional%20tech%20company%20atmosphere%2C%20soft%20natural%20lighting%2C%20Chinese%20enterprise%20style%2C%20minimal%20aesthetic%2C%20trustworthy%20feeling&width=800&height=500&seq=about-brand-story&orientation=landscape"
                  alt="BB彩票平台办公环境"
                  title="BB彩票平台办公环境"
                  className="w-full rounded-xl object-cover h-[350px]"
                />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-700 text-sm leading-relaxed">
                  <p>
                    BB彩票平台成立于2018年，是一家专注于移动互联网彩票服务的科技公司。我们致力于通过技术创新，为广大彩民提供安全、便捷、透明的线上购彩服务。
                  </p>
                  <p>
                    自成立以来，BB彩票平台始终坚持"诚信为本、用户至上"的经营理念，已累计服务超过500万用户，完成超过1.2亿笔交易订单，累计兑付奖金超过89亿元，赢得了广大彩民的信赖与好评。
                  </p>
                  <p>
                    平台持有国家相关部门颁发的合法经营资质，所有彩票销售均严格遵守国家法律法规，资金由第三方银行全程托管，确保每一笔交易的安全与透明。
                  </p>
                </div>
                <div className="mt-6 p-5 rounded-xl bg-primary-50 border border-primary-100/70">
                  <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2">
                    企业使命
                  </h3>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    让每一位彩民都能享受安全、便捷、透明的购彩体验，用科技力量推动彩票行业的健康发展。
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-8 text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {values.map((v, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70 hover:border-primary-200/70 transition-all duration-300"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                      <i className={`${v.icon} text-2xl text-primary-600`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-sm">
                      {v.title}
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-6">
                  平台资质
                </h2>
                <div className="space-y-3">
                  {certs.map((cert, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70"
                    >
                      <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-verified-badge-line text-accent-600"></i>
                      </div>
                      <span className="text-foreground-950 text-sm font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-6">
                  平台数据
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat, idx) => (
                    <div
                      key={idx}
                      className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70"
                    >
                      <div className="font-heading font-bold text-2xl md:text-3xl text-primary-500 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-foreground-500 text-xs">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div id="contact" className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-foreground-600 text-sm mb-6 leading-relaxed">
                    如有任何问题，欢迎随时与我们联系
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-foreground-500 text-xs mb-1">公司地址</div>
                        <div className="text-foreground-950 text-sm font-medium">
                          广东省广州市天河区珠江新城华夏路88号BB大厦28层
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-foreground-500 text-xs mb-1">客服热线</div>
                        <div className="text-foreground-950 text-sm font-medium">400-888-6666</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-foreground-500 text-xs mb-1">电子邮箱</div>
                        <div className="text-foreground-950 text-sm font-medium">
                          service@bbcaipiao.com
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-foreground-500 text-xs mb-1">服务时间</div>
                        <div className="text-foreground-950 text-sm font-medium">
                          周一至周日 7×24小时
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-5">在线留言</h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-check-line text-3xl text-primary-600"></i>
                      </div>
                      <p className="text-foreground-950 font-medium">
                        留言提交成功！我们会尽快与您联系。
                      </p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                          您的姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          placeholder="请输入您的姓名"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="请输入您的电子邮箱"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm text-foreground-700 mb-1.5">
                          联系电话
                        </label>
                        <input
                          id="contact-phone"
                          name="phone"
                          type="tel"
                          placeholder="请输入您的联系电话"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          placeholder="请输入您想咨询的问题或建议（限500字）"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                      </div>
                      <input
                        name="company_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="contact-hp"
                      />
                      {formStatus === 'error' && formError && (
                        <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                          {formError}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-50"
                      >
                        {submitting ? '提交中...' : '提交留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}